import { DatePicker } from '../../components/ui/date-picker';
// apps/web/src/pages/estoque/historico-material-sheet.tsx — Fase 13, Plano 07 (EST-07, EST-10, D-11).
// Sheet SOMENTE LEITURA com o histórico de movimentos de uma matéria-prima do estoque próprio. O histórico é imutável:
// não há editar nem excluir; correção é feita por estorno (outra tela). Estorno e recálculo de custo ficam visíveis.
import { Fragment, useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowDownToLine, ArrowLeftRight, ArrowUpFromLine, ClipboardCheck, Flag, RefreshCw, Undo2 } from 'lucide-react';
import type { ListaHistoricoMovimentos, HistoricoMovimentoLinha, LocalizacaoView } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Skeleton } from '../../components/ui/skeleton';
import { TablePagination } from '../../components/ui/table-pagination';
import { formatarCustoUnitario, formatarQuantidadeEstoque } from '../../lib/estoque/formatacao';
import {
  TAMANHO_PAGINA_SALDOS,
  TIPOS_FILTRO_HISTORICO,
  classesTipoMovimento,
  filtrosHistoricoVazios,
  iconeTipoMovimento,
  montarQueryHistorico,
  motivoPrecisaVerMais,
  periodoValido,
  rotuloTipoMovimento,
  sinalDoMovimento,
  type FiltrosHistorico,
  type IconeTipoMovimento,
} from '../../lib/estoque/saldos';
import { formatarDataHora } from '../../lib/vendas/formatacao';
import { CampoSelect } from '../../components/ui/campo-select';

const ICONES: Readonly<Record<IconeTipoMovimento, typeof Flag>> = {
  ArrowDownToLine,
  Flag,
  ArrowUpFromLine,
  ArrowLeftRight,
  AlertTriangle,
  Undo2,
  ClipboardCheck,
};

const ROTULO = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

export interface MaterialDoHistorico {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
}

interface HistoricoMaterialSheetProps {
  /** Material cujo histórico é mostrado; `null` mantém o Sheet fechado. */
  material: MaterialDoHistorico | null;
  localizacoes: readonly LocalizacaoView[];
  onFechar: () => void;
}

function BadgeTipo({ movimento }: { movimento: HistoricoMovimentoLinha }) {
  const Icone = ICONES[iconeTipoMovimento(movimento.tipo)];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${classesTipoMovimento(movimento.tipo)}`}>
      <Icone className="size-3" aria-hidden="true" />
      {rotuloTipoMovimento(movimento.tipo, movimento.sentido)}
    </span>
  );
}

function textoMotivo(movimento: HistoricoMovimentoLinha): string {
  const partes = [movimento.motivo, movimento.justificativa].map((parte) => (parte ?? '').trim()).filter((parte) => parte !== '');
  return [...new Set(partes)].join(' — ');
}

function Dado({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className={ROTULO}>{rotulo}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

interface LinhaMovimentoProps {
  movimento: HistoricoMovimentoLinha;
  motivoAberto: boolean;
  onAlternarMotivo: () => void;
  onVerEstorno: (estornoId: string) => void;
}

function LinhaMovimento({ movimento, motivoAberto, onAlternarMotivo, onVerEstorno }: LinhaMovimentoProps) {
  const riscado = movimento.estornado ? 'text-muted-foreground line-through' : '';
  const motivo = textoMotivo(movimento);
  const longo = motivoPrecisaVerMais(motivo);
  const destino =
    movimento.origemNome || movimento.destinoNome
      ? `${movimento.origemNome ?? '—'} → ${movimento.destinoNome ?? '—'}`
      : movimento.localizacaoNome;

  return (
    <Fragment>
      <tr id={`movimento-${movimento.id}`} className="border-t">
        <td className={`whitespace-nowrap px-3 pt-2 align-top tabular-nums ${riscado}`}>{formatarDataHora(movimento.criadoEm)}</td>
        <td className="px-3 pt-2 align-top">
          <div className="flex flex-wrap items-center gap-1">
            <BadgeTipo movimento={movimento} />
            {movimento.estornado && (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
                Estornado
              </span>
            )}
            {movimento.recalculado && (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-xs font-semibold text-slate-600">
                <RefreshCw className="size-3" aria-hidden="true" />
                Custo recalculado
              </span>
            )}
          </div>
          {movimento.estornado && movimento.estornoId && (
            <button
              type="button"
              onClick={() => onVerEstorno(movimento.estornoId as string)}
              className="mt-1 min-h-11 text-xs font-semibold text-primary underline-offset-4 hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none sm:min-h-6"
            >
              Ver estorno
            </button>
          )}
          {movimento.estornoDe && (
            <p className="mt-1 text-xs text-muted-foreground">
              Estorno de {formatarDataHora(movimento.estornoDe.criadoEm)} {rotuloTipoMovimento(movimento.estornoDe.tipo, movimento.sentido === 1 ? -1 : 1)}
            </p>
          )}
        </td>
        <td className={`whitespace-nowrap px-3 pt-2 text-right align-top tabular-nums ${riscado}`}>
          {sinalDoMovimento(movimento.sentido)}
          {formatarQuantidadeEstoque(movimento.quantidade, movimento.unidade)}
        </td>
        <td className="whitespace-nowrap px-3 pt-2 text-right align-top font-semibold tabular-nums">
          {formatarQuantidadeEstoque(movimento.saldoApos, movimento.unidade)}
        </td>
      </tr>
      <tr>
        <td colSpan={4} className="px-3 pb-2 pt-1">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
            <Dado rotulo="Origem → Destino">{destino}</Dado>
            <Dado rotulo="Custo unit.">{formatarCustoUnitario(movimento.custoUnitario, movimento.unidade)}</Dado>
            <Dado rotulo="Custo médio após">{formatarCustoUnitario(movimento.custoMedioApos, movimento.unidade)}</Dado>
            <Dado rotulo="Usuário">{movimento.usuarioNome ?? '—'}</Dado>
            <div className="col-span-2 min-w-0">
              <dt className={ROTULO}>Motivo / documento</dt>
              <dd className="text-sm">
                {motivo === '' ? (
                  '—'
                ) : (
                  <>
                    <span className={motivoAberto ? 'whitespace-pre-line break-words' : 'line-clamp-2 break-words'}>{motivo}</span>
                    {longo && (
                      <button
                        type="button"
                        onClick={onAlternarMotivo}
                        aria-expanded={motivoAberto}
                        className="min-h-11 text-xs font-semibold text-primary underline-offset-4 hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none sm:min-h-6"
                      >
                        {motivoAberto ? 'Ver menos' : 'Ver mais'}
                      </button>
                    )}
                  </>
                )}
              </dd>
            </div>
          </dl>
        </td>
      </tr>
    </Fragment>
  );
}

function ConteudoHistorico({ material, localizacoes }: { material: MaterialDoHistorico; localizacoes: readonly LocalizacaoView[] }) {
  const [filtros, setFiltros] = useState<FiltrosHistorico>(filtrosHistoricoVazios);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(TAMANHO_PAGINA_SALDOS);
  const [dados, setDados] = useState<ListaHistoricoMovimentos | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [motivosAbertos, setMotivosAbertos] = useState<ReadonlySet<string>>(new Set());

  const periodoOk = periodoValido(filtros.de, filtros.ate);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      if (!periodoOk) return;
      setCarregando(true);
      setErro(false);
      try {
        const query = montarQueryHistorico(filtros, { pagina, tamanhoPagina });
        const res = await fetch(`/api/v1/estoque/saldos/proprio/${encodeURIComponent(material.id)}/historico?${query}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ListaHistoricoMovimentos;
        if (!sinal || sinal.vivo) {
          setDados(json);
          setMotivosAbertos(new Set());
        }
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
    },
    [filtros, pagina, tamanhoPagina, material.id, periodoOk],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  function alterarFiltros(parcial: Partial<FiltrosHistorico>): void {
    setFiltros((atual) => ({ ...atual, ...parcial }));
    setPagina(1);
  }

  function alternarMotivo(id: string): void {
    setMotivosAbertos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  }

  /** Leva ao movimento de estorno se ele está na página; senão filtra por estornos para encontrá-lo. */
  function verEstorno(estornoId: string): void {
    const alvo = document.getElementById(`movimento-${estornoId}`);
    if (alvo) {
      alvo.scrollIntoView({ block: 'center' });
      return;
    }
    alterarFiltros({ tipo: 'ESTORNO' });
  }

  const itens = dados?.itens ?? [];
  const comFiltro = filtros.de !== '' || filtros.ate !== '' || filtros.tipo !== '' || filtros.localizacaoId !== '';

  return (
    <>
      <SheetHeader className="pb-0">
        <SheetTitle className="pr-8 text-xl leading-tight">
          Histórico — {material.codigo} {material.nome}
        </SheetTitle>
        <SheetDescription>Movimentos do estoque próprio, do mais recente para o mais antigo.</SheetDescription>
      </SheetHeader>

      <div className="flex flex-wrap items-end gap-3 px-4" role="group" aria-label="Filtros do histórico">
        <div className="flex flex-col gap-1">
          <label htmlFor="historico-de" className={ROTULO}>
            Período — De
          </label>
          <DatePicker id="historico-de" value={filtros.de} max={filtros.ate || undefined} onValueChange={(value) => alterarFiltros({ de: value })} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="historico-ate" className={ROTULO}>
            Até
          </label>
          <DatePicker id="historico-ate" value={filtros.ate} min={filtros.de || undefined} onValueChange={(value) => alterarFiltros({ ate: value })} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="historico-tipo" className={ROTULO}>
            Tipo
          </label>
          <CampoSelect
            id="historico-tipo"
            value={filtros.tipo}
            onValueChange={(valor) => alterarFiltros({ tipo: valor as FiltrosHistorico['tipo'] })}
            options={[{ value: '', label: 'Todos' }, ...TIPOS_FILTRO_HISTORICO.map((tipo) => ({ value: tipo, label: rotuloTipoMovimento(tipo, -1).replace(' (saída)', '') }))]}
            className="w-48"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="historico-localizacao" className={ROTULO}>
            Localização
          </label>
          <CampoSelect
            id="historico-localizacao"
            value={filtros.localizacaoId}
            onValueChange={(valor) => alterarFiltros({ localizacaoId: valor })}
            options={[{ value: '', label: 'Todas' }, ...localizacoes.map((local) => ({ value: local.id, label: local.nome }))]}
            className="w-48"
          />
        </div>
        {comFiltro && (
          <Button type="button" variant="ghost" onClick={() => alterarFiltros(filtrosHistoricoVazios())}>
            Limpar filtros
          </Button>
        )}
      </div>
      {!periodoOk && (
        <p role="alert" className="px-4 text-sm text-rose-700">
          A data inicial deve ser anterior ou igual à data final.
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-4">
        {erro ? (
          <div role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar o histórico. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </div>
        ) : carregando && !dados ? (
          <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando histórico">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : itens.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Nenhum movimento neste período.</p>
        ) : (
          <table className="w-full text-sm" aria-busy={carregando}>
            <caption className="sr-only">Histórico de movimentos de {material.codigo}</caption>
            <thead className="sticky top-0 z-10 bg-slate-100 text-left">
              <tr>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Data e hora</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Tipo</th>
                <th scope="col" className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider">Quantidade</th>
                <th scope="col" className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider">Saldo após</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((movimento) => (
                <LinhaMovimento
                  key={movimento.id}
                  movimento={movimento}
                  motivoAberto={motivosAbertos.has(movimento.id)}
                  onAlternarMotivo={() => alternarMotivo(movimento.id)}
                  onVerEstorno={verEstorno}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="sticky bottom-0 mt-auto border-t bg-background">
        {itens.length > 0 && (
          <TablePagination
            page={pagina}
            pageSize={tamanhoPagina}
            totalItems={dados?.total ?? 0}
            onPageChange={setPagina}
            onPageSizeChange={(tamanho) => {
              setTamanhoPagina(tamanho);
              setPagina(1);
            }}
            itemLabel="movimentos"
          />
        )}
        <p className="px-4 py-3 text-sm text-muted-foreground">O histórico não é editado. Correções são feitas por estorno.</p>
      </div>
    </>
  );
}

export function HistoricoMaterialSheet({ material, localizacoes, onFechar }: HistoricoMaterialSheetProps) {
  return (
    <Sheet open={material !== null} onOpenChange={(aberto) => !aberto && onFechar()}>
      <SheetContent className="w-full gap-3 sm:max-w-2xl">
        {/* key: ao trocar de material os filtros e a página recomeçam do zero */}
        {material && <ConteudoHistorico key={material.id} material={material} localizacoes={localizacoes} />}
      </SheetContent>
    </Sheet>
  );
}
